import React from 'react';
import { twMerge } from 'tailwind-merge';
interface SectionProps {
  id?: string;
  className?: string;
  containerClassName?: string;
  children: React.ReactNode;
  as?: 'section' | 'div';
  ariaLabel?: string;
}
/** Standard section: generous vertical rhythm, centred 1200px container. */
export function Section({
  id,
  className,
  containerClassName,
  children,
  as: Tag = 'section',
  ariaLabel
}: SectionProps) {
  return <Tag id={id} aria-label={ariaLabel} className={twMerge('w-full py-16 md:py-24 lg:py-28', className)} data-id="element-503">
      <div className={twMerge('mx-auto w-full max-w-content px-5 md:px-8', containerClassName)} data-id="element-504">
        {children}
      </div>
    </Tag>;
}
interface EyebrowProps {
  children: React.ReactNode;
  color?: string;
  onDark?: boolean;
  className?: string;
}
/**
 * Distinctive pill eyebrow: small leading tick + uppercase label.
 * Adds a crafted, branded feel versus a plain coloured line of text.
 */
export function Eyebrow({
  children,
  color = 'var(--ct-cyan)',
  onDark = false,
  className
}: EyebrowProps) {
  return <span className={twMerge('inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-body text-[12.5px] font-bold uppercase tracking-[0.12em]', onDark ? 'border-white/15 bg-white/5' : 'border-border bg-white shadow-sm', className)} style={{
    color: onDark ? '#fff' : color
  }} data-id="element-505">
      <span className="h-1.5 w-1.5 rounded-full" style={{
      background: color
    }} aria-hidden data-id="element-506" />
      {children}
    </span>;
}
/** Centred or left section heading block with consistent rhythm. */
export function SectionHeading({
  eyebrow,
  eyebrowColor,
  title,
  body,
  align = 'left',
  onDark = false,
  className
}: {
  eyebrow?: string;
  eyebrowColor?: string;
  title: React.ReactNode;
  body?: React.ReactNode;
  align?: 'left' | 'center';
  onDark?: boolean;
  className?: string;
}) {
  return <div className={twMerge('max-w-2xl', align === 'center' && 'mx-auto text-center', className)} data-id="element-507">
      {eyebrow && <Eyebrow color={eyebrowColor} onDark={onDark} data-id="element-508">
          {eyebrow}
        </Eyebrow>}
      <h2 className={twMerge('mt-5 font-display text-[30px] font-black leading-[1.08] tracking-tight md:text-[42px] lg:text-[46px]', onDark ? 'text-white' : 'text-navy')} data-id="element-509">
        {title}
      </h2>
      {body && <p className={twMerge('mt-5 text-[17px] leading-relaxed md:text-[19px]', onDark ? 'text-white/75' : 'text-slate')} data-id="element-510">
          {body}
        </p>}
    </div>;
}